import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Save, Loader2, Type } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { AdminLayout } from "../components/admin/AdminSidebar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEXT_GROUPS = [
  {
    label: "Announcement Bar",
    keys: [{ key: "announcement_bar", label: "Banner Text", type: "text" }],
  },
  {
    label: "Homepage — How It Works",
    keys: [
      { key: "homepage_how_it_works_1_title", label: "Step 1 Title", type: "text" },
      { key: "homepage_how_it_works_1_desc", label: "Step 1 Description", type: "text" },
      { key: "homepage_how_it_works_2_title", label: "Step 2 Title", type: "text" },
      { key: "homepage_how_it_works_2_desc", label: "Step 2 Description", type: "text" },
      { key: "homepage_how_it_works_3_title", label: "Step 3 Title", type: "text" },
      { key: "homepage_how_it_works_3_desc", label: "Step 3 Description", type: "text" },
      { key: "homepage_how_it_works_4_title", label: "Step 4 Title", type: "text" },
      { key: "homepage_how_it_works_4_desc", label: "Step 4 Description", type: "text" },
    ],
  },
  {
    label: "Homepage — Sections",
    keys: [
      { key: "homepage_category_heading", label: "Category Section Title", type: "text" },
      { key: "homepage_featured_heading", label: "Featured Section Title", type: "text" },
      { key: "homepage_featured_subheading", label: "Featured Section Subtitle", type: "text" },
      { key: "homepage_about_heading", label: "About Section Title", type: "text" },
      { key: "homepage_about_text", label: "About Section Text", type: "textarea" },
      { key: "homepage_about_cta", label: "About Section CTA", type: "text" },
    ],
  },
  {
    label: "Catalog Page",
    keys: [
      { key: "catalog_heading", label: "Page Heading", type: "text" },
      { key: "catalog_subheading", label: "Page Subheading", type: "text" },
    ],
  },
  {
    label: "Trust Signals",
    keys: [
      { key: "trust_1_title", label: "Signal 1 Title", type: "text" },
      { key: "trust_1_desc", label: "Signal 1 Description", type: "text" },
      { key: "trust_2_title", label: "Signal 2 Title", type: "text" },
      { key: "trust_2_desc", label: "Signal 2 Description", type: "text" },
    ],
  },
  {
    label: "Booking & Availability",
    keys: [
      { key: "date_picker_title", label: "Date Picker Title", type: "text" },
      { key: "date_picker_subtitle", label: "Date Picker Subtitle", type: "text" },
      { key: "availability_available", label: "Available Badge Text", type: "text" },
      { key: "availability_unavailable", label: "Unavailable Badge Text", type: "text" },
      { key: "no_date_cta", label: "No Date CTA Button", type: "text" },
      { key: "dispatch_info", label: "Dispatch Info Text", type: "text" },
    ],
  },
  {
    label: "Footer",
    keys: [
      { key: "footer_tagline", label: "Tagline", type: "text" },
      { key: "footer_sustainability", label: "Sustainability Message", type: "text" },
    ],
  },
  {
    label: "Homepage — SEO Content Block",
    keys: [
      { key: "seo_block_heading", label: "Section Heading", type: "text" },
      { key: "seo_block_intro", label: "Opening Paragraph", type: "textarea" },
      { key: "seo_block_section1_heading", label: "Section 1 Heading", type: "text" },
      { key: "seo_block_section1_text", label: "Section 1 Text", type: "textarea" },
      { key: "seo_block_section2_heading", label: "Section 2 Heading", type: "text" },
      { key: "seo_block_section2_text", label: "Section 2 Text", type: "textarea" },
      { key: "seo_block_section3_heading", label: "Section 3 Heading", type: "text" },
      { key: "seo_block_section3_text", label: "Section 3 Text", type: "textarea" },
      { key: "seo_block_section4_heading", label: "Section 4 Heading", type: "text" },
      { key: "seo_block_section4_text", label: "Section 4 Text", type: "textarea" },
      { key: "seo_block_closing", label: "Closing Paragraph", type: "textarea" },
    ],
  },
  // ── Site Texts Patch 1 — Trial Booking page ─────────────────────
  {
    label: "Trial Booking Page",
    keys: [
      { key: "trial_booking_heading", label: "Page Heading", type: "text" },
      { key: "trial_booking_subheading", label: "Subheading", type: "text" },
      { key: "trial_booking_intro", label: "Intro Paragraph", type: "textarea" },
      { key: "trial_booking_help", label: "Help Line (below intro)", type: "text" },
      { key: "trial_booking_form_heading", label: "Details Section Heading", type: "text" },
      { key: "trial_booking_choose_time_heading", label: "Choose Time Heading", type: "text" },
      { key: "trial_booking_no_slots_heading", label: "No-slots Heading", type: "text" },
      { key: "trial_booking_no_slots_hint", label: "No-slots Hint", type: "textarea" },
      { key: "trial_booking_large_gap_hint", label: "14-day-gap Hint", type: "textarea" },
      { key: "trial_booking_submit_cta", label: "Confirm Booking Button", type: "text" },
      { key: "trial_booking_enquiry_submit_cta", label: "Send Enquiry Button", type: "text" },
    ],
  },
  // ── Site Texts Patch 1 — Trial Booking Success ─────────────────
  {
    label: "Trial Booking — Success & Errors",
    keys: [
      { key: "trial_success_heading", label: "Success Heading", type: "text" },
      { key: "trial_success_subheading", label: "Success Subheading", type: "text" },
      { key: "trial_success_polling_heading", label: "Polling Heading", type: "text" },
      { key: "trial_success_polling_subheading", label: "Polling Message", type: "textarea" },
      { key: "trial_success_refunded_heading", label: "Refund Heading", type: "text" },
      { key: "trial_success_refunded_message", label: "Refund Message", type: "textarea" },
      { key: "trial_success_refunded_cta", label: "Refund CTA (Rebook button)", type: "text" },
      { key: "trial_success_error_heading", label: "Error Heading", type: "text" },
      { key: "trial_success_error_message", label: "Error Message", type: "textarea" },
      { key: "trial_success_continue_cta", label: "Continue Browsing Link", type: "text" },
      { key: "trial_success_calendar_heading", label: "Calendar Section Heading", type: "text" },
      { key: "trial_success_calendar_footnote", label: "Calendar Footnote", type: "textarea" },
    ],
  },
  // ── Site Texts Patch 1 — Customer Portal empty states ──────────
  {
    label: "Customer Portal — Empty States",
    keys: [
      { key: "portal_bookings_title", label: "Portal Title", type: "text" },
      { key: "portal_bookings_empty_heading", label: "Rental — Empty Heading", type: "text" },
      { key: "portal_bookings_empty_cta", label: "Rental — Empty CTA (Browse button)", type: "text" },
      { key: "portal_trials_empty_heading", label: "Trials — Empty Heading", type: "text" },
      { key: "portal_trials_empty_body", label: "Trials — Empty Body", type: "textarea" },
      { key: "portal_trials_empty_cta", label: "Trials — Empty CTA (Book Trial button)", type: "text" },
    ],
  },
];

export default function AdminSiteTexts() {
  const [texts, setTexts] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) { navigate("/admin/login"); return; }
    fetchTexts();
  }, [navigate]);

  const fetchTexts = async () => {
    try {
      const res = await axios.get(`${API}/settings/site-texts`);
      setTexts(res.data.texts || {});
    } catch {} finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("adminToken");
      await axios.put(`${API}/settings/site-texts`, { texts }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Site texts saved");
    } catch { toast.error("Failed to save"); } finally { setSaving(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#9A7B4F]" /></div>;

  return (
    <AdminLayout>
      <div className="max-w-[900px] mx-auto px-4 lg:px-8 py-8 lg:py-12">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Link to="/admin/dashboard" className="text-[#676664]/40 hover:text-[#676664]">
              <ArrowLeft className="w-4 h-4" />
            </Link>
            <div className="flex items-center gap-2">
              <Type className="w-4 h-4 text-[#9A7B4F]" />
              <h1 className="text-xl text-[#282828] font-light">Site Text Editor</h1>
            </div>
          </div>
          <Button onClick={handleSave} disabled={saving} className="bg-[#282828] hover:bg-[#1a1a1a] text-white text-[11px] tracking-[0.12em] uppercase px-6" data-testid="save-texts-btn">
            <Save className="w-3.5 h-3.5 mr-2" /> {saving ? "Saving..." : "Save All"}
          </Button>
        </div>

        <div className="space-y-6">
          {TEXT_GROUPS.map((group) => (
            <div key={group.label} className="bg-white border border-[#282828]/8 p-5 lg:p-6">
              <h2 className="text-[11px] text-[#9A7B4F] tracking-[0.15em] uppercase mb-4 font-medium">{group.label}</h2>
              <div className="space-y-3">
                {group.keys.map(({ key, label, type }) => (
                  <div key={key}>
                    <label className="text-[10px] text-[#676664]/60 tracking-[0.08em] uppercase block mb-1">{label}</label>
                    {type === "textarea" ? (
                      <Textarea
                        value={texts[key] || ""}
                        onChange={(e) => setTexts(t => ({ ...t, [key]: e.target.value }))}
                        className="border-[#282828]/10 text-[13px] min-h-[80px]"
                        data-testid={`text-${key}`}
                      />
                    ) : (
                      <Input
                        value={texts[key] || ""}
                        onChange={(e) => setTexts(t => ({ ...t, [key]: e.target.value }))}
                        className="border-[#282828]/10 text-[13px] py-4"
                        data-testid={`text-${key}`}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
}
